import { Controller } from "@hotwired/stimulus"
import flash from "../lib/flashes.js";
import { withCsrf } from "../lib/hudini/csrf.js"
import { initTooltips } from "../lib/hudini/tooltips.js"

// Manages the file/record context chips staged in the composer: attaching via
// the button, drag-and-drop, or paste, removing chips, and clearing them when a
// message is sent (the server commits them onto the outgoing turn). The chip
// list is a target rather than a global id so multiple panels can't collide.
export default class extends Controller {
  static targets = ["list", "fileInput"]
  static values = {
    contextUrl: String,
  }

  // --- Files -----------------------------------------------------------------

  attachFile(event) {
    event.preventDefault()
    this.fileInputTarget.click()
  }

  async uploadFile() {
    await this.uploadFiles(this.fileInputTarget.files)
    this.fileInputTarget.value = ""
  }

  handleDragEnter(event) {
    if (!this.eventHasFiles(event)) return

    event.preventDefault()
    this.dragDepth = (this.dragDepth || 0) + 1
    event.currentTarget.classList.add("hudini__field--dragging")
  }

  handleDragOver(event) {
    if (!this.eventHasFiles(event)) return

    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
    event.currentTarget.classList.add("hudini__field--dragging")
  }

  handleDragLeave(event) {
    if (!this.eventHasFiles(event)) return

    event.preventDefault()
    this.dragDepth = Math.max((this.dragDepth || 1) - 1, 0)
    if (this.dragDepth === 0) event.currentTarget.classList.remove("hudini__field--dragging")
  }

  async handleDrop(event) {
    if (!this.eventHasFiles(event)) return

    event.preventDefault()
    this.dragDepth = 0
    event.currentTarget.classList.remove("hudini__field--dragging")
    await this.uploadFiles(this.filesFromTransfer(event.dataTransfer))
  }

  async handlePaste(event) {
    const files = this.filesFromTransfer(event.clipboardData)
    if (files.length === 0) return

    event.preventDefault()
    await this.uploadFiles(files)
  }

  async uploadFiles(files) {
    for (const file of Array.from(files || []).filter(Boolean)) {
      const body = new FormData()
      body.append("file", file)
      await this.updateContext(this.contextUrlValue, { method: "POST", body })
    }
  }

  eventHasFiles(event) {
    const transfer = event.dataTransfer
    if (!transfer) return false

    return Array.from(transfer.types || []).includes("Files") ||
      Array.from(transfer.items || []).some(item => item.kind === "file") ||
      (transfer.files && transfer.files.length > 0)
  }

  filesFromTransfer(transfer) {
    if (!transfer) return []

    const files = Array.from(transfer.files || [])
    if (files.length > 0) return files

    return Array.from(transfer.items || [])
      .filter(item => item.kind === "file")
      .map(item => item.getAsFile())
      .filter(Boolean)
  }

  // --- Removal / clearing ----------------------------------------------------

  async removeContext(event) {
    event.preventDefault()
    const index = event.currentTarget.dataset.index
    await this.updateContext(`${this.contextUrlValue}/${index}`, { method: "DELETE" })
  }

  // Clears the staged chips when a message is sent. The server has already
  // committed them onto the outgoing turn (and renders them there), so the
  // composer just needs to empty its staging list.
  clearPending() {
    if (!this.hasListTarget) return

    this.listTarget.innerHTML = ""
    this.listTarget.classList.add("hidden")
  }

  async updateContext(url, options) {
    const response = await fetch(url, withCsrf(options))
    if (!response.ok) {
      flash("Could not update context", "error")
      return
    }
    const html = await response.text()
    if (this.hasListTarget) this.listTarget.outerHTML = html
    initTooltips(this.element)
  }
}
